import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { callApi } from '../api';
import { Section, Stat, ErrorBox, LoadingSpinner } from '../components/Section';
import { UserChip } from '../components/UserChip';
import { WindowChips, windowLabel } from '../components/WindowChips';
import { useSession } from '../hooks/useSession';
import { timeAgo } from '../utils';

export function Invites() {
  const { session } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const rootUserId = searchParams.get('root') || null;
  const [days, setDays] = useState(30);
  const [graph, setGraph] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const graphUrl = rootUserId
      ? `/api/v1/admin/invites/graph?depth=4&root_user_id=${encodeURIComponent(rootUserId)}`
      : '/api/v1/admin/invites/graph?depth=4';
    Promise.all([
      callApi(graphUrl, { accessToken: session.access_token }),
      callApi(`/api/v1/admin/invites/stats?days=${days}`, {
        accessToken: session.access_token,
      }),
    ])
      .then(([g, s]) => { setGraph(g); setStats(s); })
      .catch((e) => setError(e.message));
  }, [days, rootUserId, session.access_token]);

  if (error && !graph && !stats) return <ErrorBox message={error} />;
  if (!graph || !stats) return <LoadingSpinner />;

  const reroot = (userId) => setSearchParams({ root: userId });
  const resetRoot = () => setSearchParams({});

  const rootNode = graph.nodes.find((n) => n.parent_id === null);
  const treeTitle = rootUserId
    ? `Tree (rooted at ${rootNode?.display_name || rootUserId.slice(0, 8)})`
    : 'Tree (rooted at you)';

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-stone-800">Invite graph</h1>

      <Section
        title={`Stats (${windowLabel(days)})`}
        action={<WindowChips value={days} onChange={setDays} />}
      >
        <div className="grid grid-cols-3 gap-4">
          <Stat value={stats.total_sent} label="invites sent" />
          <Stat value={stats.total_accepted} label="accepted" />
          <Stat
            value={`${Math.round(stats.overall_conversion_rate * 100)}%`}
            label="conversion"
          />
        </div>
      </Section>

      <Section title="Top inviters">
        {stats.top_inviters.length === 0 ? (
          <p className="text-sm text-stone-500">No invites yet.</p>
        ) : (
          <div className="divide-y">
            {stats.top_inviters.map((r) => (
              <button
                key={r.user_id}
                onClick={() => reroot(r.user_id)}
                className="w-full flex items-center justify-between py-1.5 hover:bg-stone-50 text-left"
                title="Click to re-root the tree at this user"
              >
                <UserChip userId={r.user_id} displayName={r.display_name} />
                <span className="text-xs text-stone-500">
                  {r.invites_sent} sent · {r.invites_accepted} accepted
                  {r.avg_hours_to_accept != null && ` · ${r.avg_hours_to_accept.toFixed(1)}h avg`}
                </span>
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section
        title={treeTitle}
        action={
          rootUserId && (
            <button
              onClick={resetRoot}
              className="text-xs text-stone-600 underline"
            >
              Back to me
            </button>
          )
        }
      >
        <Tree nodes={graph.nodes} onReroot={reroot} />
      </Section>
    </div>
  );
}

// Flat (parent_id, depth) list rendered as nested <ul>. The depth cap (6 server-side)
// keeps the recursive walk bounded.
function Tree({ nodes, onReroot }) {
  if (nodes.length === 0) return <p className="text-sm text-stone-500">No data.</p>;

  const childrenOf = (id) => nodes.filter((n) => n.parent_id === id);
  const root = nodes.find((n) => n.parent_id === null);
  if (!root) return <p className="text-sm text-stone-500">No root.</p>;

  const renderNode = (node) => {
    const children = childrenOf(node.user_id);
    return (
      <li key={node.user_id} className="ml-4">
        <div className="flex items-center justify-between py-1 text-sm">
          <UserChip userId={node.user_id} displayName={node.display_name} />
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-500">
              {node.invited_count} invited · {node.accepted_count} accepted
              {node.accepted_at && ` · accepted ${timeAgo(node.accepted_at)}`}
            </span>
            {node.invited_count > 0 && node.parent_id !== null && (
              <button
                onClick={() => onReroot(node.user_id)}
                className="text-xs text-stone-500 underline whitespace-nowrap"
              >
                drill in
              </button>
            )}
          </div>
        </div>
        {children.length > 0 && (
          <ul className="border-l border-stone-200">
            {children.map(renderNode)}
          </ul>
        )}
      </li>
    );
  };

  return <ul>{renderNode(root)}</ul>;
}
